import type { ConsentLinkState } from '@/lib/consent-public';

// HW-1024: what the public confirm page says for every link state except
// PENDING, which renders the consent form itself. The candidate reads this —
// plain words, no internal status names.
export const CONSENT_LINK_COPY: Record<
  Exclude<ConsentLinkState, 'PENDING'>,
  { title: string; message: string; tone: 'success' | 'neutral' }
> = {
  CONFIRMED: {
    title: 'Your consent is recorded',
    message: 'There is nothing more to do. You can close this page.',
    tone: 'success',
  },
  // HW-1057: shown with a "Withdraw consent" button, like CONFIRMED.
  ENDED: {
    title: 'Your consent has ended',
    message:
      'It was valid for a year. You can withdraw it below — we then stop using your data and erase it.',
    tone: 'neutral',
  },
  EXPIRED: {
    title: 'This link has expired',
    message: 'Please write to us and we will send you a new one.',
    tone: 'neutral',
  },
  SUPERSEDED: {
    title: 'This link is no longer active',
    message: 'We sent you a newer email. Please use the link from the most recent one.',
    tone: 'neutral',
  },
  // HW-1057: consent was withdrawn (by the candidate or at their request) — no
  // invitation to consent again (HW-1055: never asked again). An ended consent
  // has its own ENDED copy.
  REVOKED: {
    title: 'Your consent is withdrawn',
    message: 'We no longer use your data. If you have questions, please write to us.',
    tone: 'neutral',
  },
  NOT_FOUND: {
    title: 'Link not found',
    message: 'This link is invalid or incomplete. Please open it directly from your email.',
    tone: 'neutral',
  },
};

// When the page could not read the link at all. The public endpoints allow
// 30 reads, 10 confirms and 10 withdrawals per hour per IP address, so a 429
// gets its own words.
export const CONSENT_PAGE_FAILURE_COPY = {
  FAILED: {
    title: 'Something went wrong',
    message: 'We could not open this page. Please try again in a moment.',
    tone: 'neutral',
  },
  RATE_LIMITED: {
    title: 'Too many attempts',
    message: 'Please wait an hour, then open the link from your email again.',
    tone: 'neutral',
  },
  UNSUPPORTED: {
    title: 'This link cannot be opened here',
    message: 'Please write to us and we will help.',
    tone: 'neutral',
  },
  // HW-1057: after the candidate withdrew consent from this page.
  WITHDRAWN: {
    title: 'Your consent is withdrawn',
    message: 'We will no longer use your data and will erase it. You can close this page.',
    tone: 'success',
  },
} as const;

export const CONSENT_SUBMIT_FAILED_MESSAGE = 'Something went wrong. Please try again in a moment.';
// HW-1057: a withdrawal the page cannot make — write to the team instead.
export const CONSENT_WITHDRAW_WRITE_TO_US_MESSAGE =
  'Your consent cannot be withdrawn here. Please write to us and we will do it.';
// HW-1057: the consent changed while the page was open.
export const CONSENT_WITHDRAW_CHANGED_MESSAGE =
  'Your consent changed meanwhile. Please open the link from your email again.';
export const CONSENT_SUBMIT_RATE_LIMITED_MESSAGE =
  'Too many attempts from your network. Please wait an hour and try again.';
